import React from 'react';
import PropTypes from 'prop-types';

import { Stack, Typography, Alert, Box, Tooltip, AlertTriangleFillIcon } from '@metarouter-private/mr-mui';

export default function DeployProgressContent({ isDeploying, completed, total, failedDeployments }) {
  const hasFailures = failedDeployments.length > 0;

  return (
    <Stack spacing={2}>
      {isDeploying ? (
        <Alert variant="filled" severity="warning">
          <Typography variant="subtitle1">
            Deployment in progress. Please do not navigate away from this page or close your browser to avoid
            interrupting the deployment process.
          </Typography>
        </Alert>
      ) : (
        <Alert variant="filled" severity={hasFailures ? 'warning' : 'success'}>
          <Typography variant="subtitle1">
            {hasFailures
              ? 'Deployment finished. Some pipelines failed to deploy — see below.'
              : 'Deployment complete. All selected pipelines deployed successfully.'}
          </Typography>
        </Alert>
      )}

      <Typography variant="h6" data-test-id="deployProgressCount">
        {completed} of {total} deployments concluded
      </Typography>

      {hasFailures && (
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Failed Deployments
          </Typography>

          <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
            <Stack spacing={1} data-test-id="failedDeploymentsList">
              {failedDeployments.map(failure => (
                <Stack key={failure.pipelineId} direction="row" spacing={1} alignItems="center">
                  <AlertTriangleFillIcon color="error" fontSize="small" />
                  <Typography variant="body2">{failure.name}</Typography>
                  <Box flexGrow={1} />
                  <Tooltip title={failure.error || 'Unknown error'}>
                    <Box
                      component="span"
                      sx={{
                        cursor: 'help',
                        color: 'error.main',
                        textDecoration: 'underline dotted',
                        fontSize: '0.875rem',
                      }}
                    >
                      Why?
                    </Box>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

DeployProgressContent.propTypes = {
  isDeploying: PropTypes.bool.isRequired,
  completed: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  failedDeployments: PropTypes.arrayOf(
    PropTypes.shape({
      pipelineId: PropTypes.string,
      name: PropTypes.string,
      error: PropTypes.string,
    })
  ).isRequired,
};
